import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, EyeIcon, EditIcon, TrashIcon, CheckIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { DatePicker } from '../../components/ui/DatePicker';
import { Badge } from '../../components/ui/Badge';
interface BankTransaction {
  id: string;
  voucherNo: string;
  transactionType: 'Withdrawal' | 'Deposit' | 'Bank Transfer';
  bankName: string;
  bankAccount: string;
  amount: number;
  voucherDate: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  createdBy: string;
  createdAt: string;
  checkNo?: string;
  fromBankName?: string;
  fromBankAccount?: string;
  toBankName?: string;
  toBankAccount?: string;
}
export function BankTransactionList() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    transactionType: '',
    bankName: '',
    status: '',
    search: ''
  });
  const userRole = 'Finance Manager';
  useEffect(() => {
    loadTransactions();
  }, [filters]);
  const loadTransactions = async () => {
    setLoading(true);
    try {
      const mockData: BankTransaction[] = [{
        id: '1',
        voucherNo: 'BT-2024-001',
        transactionType: 'Withdrawal',
        bankName: 'Premier Bank',
        bankAccount: 'Hargeisa Branch - 1234567890',
        amount: 50000,
        voucherDate: '2024-01-15',
        status: 'Pending',
        createdBy: 'John Doe',
        createdAt: '2024-01-15 10:30 AM',
        checkNo: 'CHK-001'
      }, {
        id: '2',
        voucherNo: 'BT-2024-002',
        transactionType: 'Deposit',
        bankName: 'Dahabshiil Bank',
        bankAccount: 'Mogadishu Branch - 9876543210',
        amount: 75000,
        voucherDate: '2024-01-16',
        status: 'Approved',
        createdBy: 'Jane Smith',
        createdAt: '2024-01-16 02:15 PM'
      }, {
        id: '3',
        voucherNo: 'BT-2024-003',
        transactionType: 'Bank Transfer',
        bankName: 'Premier Bank',
        bankAccount: 'Hargeisa Branch - 1234567890',
        fromBankName: 'Premier Bank',
        fromBankAccount: 'Hargeisa Branch - 1234567890',
        toBankName: 'Dahabshiil Bank',
        toBankAccount: 'Berbera Branch - 5555555555',
        amount: 100000,
        voucherDate: '2024-01-17',
        status: 'Pending',
        createdBy: 'Ahmed Ali',
        createdAt: '2024-01-17 11:00 AM'
      }];
      setTransactions(mockData);
    } catch (error) {
      console.error('Failed to load bank transactions:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this bank transaction?')) {
      try {
        console.log('Deleting transaction:', id);
        loadTransactions();
      } catch (error: any) {
        alert(error.message);
      }
    }
  };
  const handleApprove = async (id: string) => {
    try {
      console.log('Approving transaction:', id);
      loadTransactions();
    } catch (error: any) {
      alert(error.message);
    }
  };
  const canEdit = (status: string) => {
    return status === 'Pending' && (userRole === 'Accountant' || userRole === 'Finance Manager');
  };
  const canDelete = (status: string) => {
    return status === 'Pending' && (userRole === 'Accountant' || userRole === 'Finance Manager');
  };
  const canApprove = () => {
    return userRole === 'Finance Manager';
  };
  const transactionTypeOptions = [{
    value: 'Withdrawal',
    label: 'Withdrawal'
  }, {
    value: 'Deposit',
    label: 'Deposit'
  }, {
    value: 'Bank Transfer',
    label: 'Bank Transfer'
  }];
  const statusOptions = [{
    value: 'Pending',
    label: 'Pending'
  }, {
    value: 'Approved',
    label: 'Approved'
  }, {
    value: 'Rejected',
    label: 'Rejected'
  }];
  const mockBankNames = [{
    value: 'Premier Bank',
    label: 'Premier Bank'
  }, {
    value: 'Dahabshiil Bank',
    label: 'Dahabshiil Bank'
  }, {
    value: 'Salaam Bank',
    label: 'Salaam Bank'
  }];
  const getStatusVariant = (status: string): 'success' | 'warning' | 'danger' | 'default' => {
    const map: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
      Pending: 'warning',
      Approved: 'success',
      Rejected: 'danger'
    };
    return map[status] || 'default';
  };
  const getTypeColor = (type: string): string => {
    const map: Record<string, string> = {
      Withdrawal: 'bg-red-100 text-red-800',
      Deposit: 'bg-green-100 text-green-800',
      'Bank Transfer': 'bg-blue-100 text-blue-800'
    };
    return map[type] || 'bg-gray-100 text-gray-800';
  };
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-484">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-485">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-486">
          <div data-id="element-487">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-488">
              Bank Transaction List
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-489">
              Manage bank withdrawals, deposits, and transfers
            </p>
          </div>
          <div className="flex gap-2" data-id="element-490">
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)} data-id="element-491">
              <FilterIcon className="w-4 h-4 mr-2" data-id="element-492" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
            {userRole !== 'Auditor' && <Button onClick={() => navigate('/bank-transaction/create')} data-id="element-493">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-494" />
                Create Bank Transaction
              </Button>}
          </div>
        </div>

        {/* Filters */}
        {showFilters && <div className="bg-white p-4 rounded-lg border space-y-4" data-id="element-495">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-496">
              Search Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-497">
              <Input label="Search" placeholder="Voucher No, Amount, Check No..." value={filters.search} onChange={e => setFilters({
            ...filters,
            search: e.target.value
          })} data-id="element-498" />

              <Select label="Transaction Type" options={transactionTypeOptions} value={filters.transactionType} onChange={e => setFilters({
            ...filters,
            transactionType: e.target.value
          })} data-id="element-499" />

              <Select label="Bank Name" options={mockBankNames} value={filters.bankName} onChange={e => setFilters({
            ...filters,
            bankName: e.target.value
          })} data-id="element-500" />

              <Select label="Status" options={statusOptions} value={filters.status} onChange={e => setFilters({
            ...filters,
            status: e.target.value
          })} data-id="element-501" />

              <DatePicker label="Date From" value={filters.dateFrom} onChange={date => setFilters({
            ...filters,
            dateFrom: date
          })} data-id="element-502" />

              <DatePicker label="Date To" value={filters.dateTo} onChange={date => setFilters({
            ...filters,
            dateTo: date
          })} data-id="element-503" />
            </div>
            <div className="flex justify-end" data-id="element-504">
              <Button variant="secondary" size="sm" onClick={() => setFilters({
            dateFrom: '',
            dateTo: '',
            transactionType: '',
            bankName: '',
            status: '',
            search: ''
          })} data-id="element-505">
                Clear Filters
              </Button>
            </div>
          </div>}

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden" data-id="element-506">
          {loading ? <div className="p-12 text-center text-gray-500" data-id="element-507">
              Loading bank transactions...
            </div> : <div className="overflow-x-auto" data-id="element-508">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-509">
                <thead className="bg-gray-50" data-id="element-510">
                  <tr data-id="element-511">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-512">
                      Voucher No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-513">
                      Transaction Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-514">
                      Bank Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-515">
                      Bank Account
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-516">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-517">
                      Voucher Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-518">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-519">
                      Created By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-520">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-521">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-522">
                  {transactions.length === 0 ? <tr data-id="element-523">
                      <td colSpan={10} className="px-6 py-12 text-center text-gray-500" data-id="element-524">
                        No bank transactions found. Click "Create Bank
                        Transaction" to add one.
                      </td>
                    </tr> : transactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50" data-id="element-525">
                        <td className="px-6 py-4" data-id="element-526">
                          <button onClick={() => navigate(`/bank-transaction/${transaction.id}`)} className="text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-527">
                            {transaction.voucherNo}
                          </button>
                        </td>
                        <td className="px-6 py-4" data-id="element-528">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getTypeColor(transaction.transactionType)}`} data-id="element-529">
                            {transaction.transactionType}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-530">
                          {transaction.bankName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-531">
                          {transaction.bankAccount}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium" data-id="element-532">
                          ${transaction.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-533">
                          {new Date(transaction.voucherDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4" data-id="element-534">
                          <Badge variant={getStatusVariant(transaction.status)} data-id="element-535">
                            {transaction.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-536">
                          {transaction.createdBy}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-537">
                          {transaction.createdAt}
                        </td>
                        <td className="px-6 py-4" data-id="element-538">
                          <div className="flex items-center justify-center gap-2" data-id="element-539">
                            <button onClick={() => navigate(`/bank-transaction/${transaction.id}`)} className="text-gray-600 hover:text-gray-900" title="View" data-id="element-540">
                              <EyeIcon className="w-4 h-4" data-id="element-541" />
                            </button>
                            {canEdit(transaction.status) && <button onClick={() => navigate(`/bank-transaction/${transaction.id}/edit`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-542">
                                <EditIcon className="w-4 h-4" data-id="element-543" />
                              </button>}
                            {canDelete(transaction.status) && <button onClick={() => handleDelete(transaction.id)} className="text-red-600 hover:text-red-800" title="Delete" data-id="element-544">
                                <TrashIcon className="w-4 h-4" data-id="element-545" />
                              </button>}
                            {canApprove() && transaction.status === 'Pending' && <button onClick={() => handleApprove(transaction.id)} className="text-green-600 hover:text-green-800" title="Approve" data-id="element-546">
                                  <CheckIcon className="w-4 h-4" data-id="element-547" />
                                </button>}
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}